import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import Login from './components/Login';
import Layout from './components/Layout';
import PlaylistsPage from './components/PlaylistsPage';
import PlaylistChannelsPage from './components/PlaylistChannelsPage';
import ChannelEditPage from './components/ChannelEditPage';
import ApplicationsPage from './components/ApplicationsPage';
import ApplicationEditPage from './components/ApplicationEditPage';
import SettingsPage from './components/SettingsPage';
import PlaylistEditPage from './components/PlaylistEditPage';
import AdsPage from './components/AdsPage';
import AdBlockEditPage from './components/AdBlockEditPage';
import AdsSettingsPage from './components/AdsSettingsPage';
import RadioPlaylistsPage from './pages/RadioPlaylistsPage';
import RadioPlaylistChannelsPage from './pages/RadioPlaylistChannelsPage';
import RadioChannelEditPage from './pages/RadioChannelEditPage';
import AdminUsersPage from './pages/AdminUsersPage';
import AdminLogsPage from './pages/AdminLogsPage';
import MonitoringPage from './pages/MonitoringPage';
import AdBlockingPage from './pages/AdBlockingPage';
import AppUpdatesPage from './pages/AppUpdatesPage';
import TVGuidePage from './pages/TVGuidePage';
import AllChannelsPage from './pages/AllChannelsPage';

const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
  },
});

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    console.log('App: Checking authentication, token exists:', !!token);
    if (token) {
      setIsAuthenticated(true);
    }
    setIsLoading(false);
  }, []);

  const handleLogin = () => {
    setIsAuthenticated(true);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    setIsAuthenticated(false);
  };

  const ProtectedRoute = ({ children }) => {
    if (isLoading) {
      return <div>Загрузка...</div>; // Или компонент загрузки
    }
    return isAuthenticated ? children : <Navigate to="/login" replace />;
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <Routes>
          <Route 
            path="/login" 
            element={
              isAuthenticated ? 
                <Navigate to="/playlists" replace /> : 
                <Login onLogin={handleLogin} />
            } 
          />
          <Route 
            path="/applications" 
            element={
              <ProtectedRoute>
                <Layout onLogout={handleLogout}>
                  <ApplicationsPage />
                </Layout>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/applications/:id" 
            element={
              <ProtectedRoute>
                <Layout onLogout={handleLogout}>
                  <ApplicationEditPage />
                </Layout>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/playlists" 
            element={
              <ProtectedRoute>
                <Layout onLogout={handleLogout}>
                  <PlaylistsPage />
                </Layout>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/playlists/:id" 
            element={
              <ProtectedRoute>
                <Layout onLogout={handleLogout}>
                  <PlaylistEditPage />
                </Layout>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/playlists/:playlistId/channels" 
            element={
              <ProtectedRoute>
                <Layout onLogout={handleLogout}>
                  <PlaylistChannelsPage />
                </Layout>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/playlists/:playlistId/channels/:channelId" 
            element={
              <ProtectedRoute>
                <Layout onLogout={handleLogout}>
                  <ChannelEditPage />
                </Layout>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/channels" 
            element={
              <ProtectedRoute>
                <Layout onLogout={handleLogout}>
                  <AllChannelsPage />
                </Layout>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/channels/:id/edit" 
            element={
              <ProtectedRoute>
                <Layout onLogout={handleLogout}>
                  <ChannelEditPage />
                </Layout>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/ads" 
            element={
              <ProtectedRoute>
                <Layout onLogout={handleLogout}>
                  <AdsPage />
                </Layout>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/ads/settings" 
            element={
              <ProtectedRoute>
                <Layout onLogout={handleLogout}>
                  <AdsSettingsPage />
                </Layout>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/ads/:id" 
            element={
              <ProtectedRoute>
                <Layout onLogout={handleLogout}>
                  <AdBlockEditPage />
                </Layout>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/radio/playlists" 
            element={
              <ProtectedRoute>
                <Layout onLogout={handleLogout}>
                  <RadioPlaylistsPage />
                </Layout>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/radio/playlists/:playlistId/channels" 
            element={
              <ProtectedRoute>
                <Layout onLogout={handleLogout}>
                  <RadioPlaylistChannelsPage />
                </Layout>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/radio/playlists/:playlistId/channels/:channelId/edit" 
            element={
              <ProtectedRoute>
                <Layout onLogout={handleLogout}>
                  <RadioChannelEditPage />
                </Layout>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/radio/channels/new" 
            element={
              <ProtectedRoute>
                <Layout onLogout={handleLogout}>
                  <RadioChannelEditPage />
                </Layout>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/settings" 
            element={
              <ProtectedRoute>
                <Layout onLogout={handleLogout}>
                  <SettingsPage />
                </Layout>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin-users" 
            element={
              <ProtectedRoute>
                <Layout onLogout={handleLogout}>
                  <AdminUsersPage />
                </Layout>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin-logs" 
            element={
              <ProtectedRoute>
                <Layout onLogout={handleLogout}>
                  <AdminLogsPage />
                </Layout>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/monitoring" 
            element={
              <ProtectedRoute>
                <Layout onLogout={handleLogout}>
                  <MonitoringPage />
                </Layout>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/ad-blocking" 
            element={
              <ProtectedRoute>
                <Layout onLogout={handleLogout}>
                  <AdBlockingPage />
                </Layout>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/app-updates" 
            element={
              <ProtectedRoute>
                <Layout onLogout={handleLogout}>
                  <AppUpdatesPage />
                </Layout>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/tv-guide" 
            element={
              <ProtectedRoute>
                <Layout onLogout={handleLogout}>
                  <TVGuidePage />
                </Layout>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/" 
            element={<Navigate to={isAuthenticated ? "/playlists" : "/login"} replace />} 
          />
        </Routes>
      </Router>
    </ThemeProvider>
  );
}

export default App;
